import React from 'react';
import {
  Document, Page, View, Text, Image, StyleSheet,
} from '@react-pdf/renderer';
import { saudiCities } from '../../data/saudiCities';
import { registerCairoFont, safePdfText } from './pdfTextUtils';

registerCairoFont();

// ─── Colors ───────────────────────────────────────────────────────────────────
const NAVY  = '#112653';
const BLUE  = '#1E50A2';
const LGRAY = '#F0F2F5';
const MGRAY = '#D2D5DC';
const DGRAY = '#6B7280';
const WHITE = '#FFFFFF';
const BLACK = '#141414';
const RED   = '#DC2626';
const GREEN = '#16A34A';

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = StyleSheet.create({
  page:       { fontFamily: 'Cairo', fontSize: 9, color: BLACK, backgroundColor: WHITE, paddingBottom: 48 },
  topBar:     { height: 6, backgroundColor: NAVY },

  // Header
  headerRow:  { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10 16 8 16' },
  logoImg:    { width: 52, height: 52, objectFit: 'contain', borderRadius: 4 },
  logoPlaceholder: { width: 52, height: 52, backgroundColor: LGRAY, borderRadius: 4, justifyContent: 'center', alignItems: 'center' },
  companyBlock: { flex: 1, marginLeft: 10, marginRight: 10 },
  companyName:  { fontSize: 14, color: NAVY },
  companyMetaLine: { fontSize: 7.5, color: DGRAY, marginTop: 2 },
  barcodeBlock: { alignItems: 'center' },
  barcodeImg:   { width: 110, height: 38 },
  barcodeNum:   { fontSize: 8, color: NAVY, marginTop: 3, textAlign: 'center' },

  divider:    { height: 1.5, backgroundColor: NAVY, marginHorizontal: 16 },
  thinLine:   { height: 0.5, backgroundColor: MGRAY },

  // Title
  titleBar:   { backgroundColor: BLUE, paddingVertical: 5, marginHorizontal: 16, marginTop: 6 },
  titleText:  { fontSize: 12, color: WHITE, textAlign: 'center', letterSpacing: 1 },

  // Doc meta
  metaBar:    { flexDirection: 'row', justifyContent: 'space-between', backgroundColor: LGRAY, paddingHorizontal: 10, paddingVertical: 3, marginHorizontal: 16, borderWidth: 0.5, borderColor: MGRAY },
  metaText:   { fontSize: 7.5, color: NAVY },

  // Section header
  secBar:     { backgroundColor: NAVY, paddingVertical: 3.5, paddingHorizontal: 10 },
  secText:    { fontSize: 8, color: WHITE },

  // Party row
  partyRow:   { flexDirection: 'row', marginHorizontal: 16, marginTop: 6 },
  partyBox:   { flex: 1, borderWidth: 0.5, borderColor: MGRAY },
  partyHead:  { backgroundColor: NAVY, paddingVertical: 3, paddingHorizontal: 8 },
  partyHeadTxt: { fontSize: 8, color: WHITE, textAlign: 'center' },
  partyBody:  { padding: 6, minHeight: 32 },
  partyName:  { fontSize: 8.8, color: BLACK },
  partySub:   { fontSize: 7, color: DGRAY, marginTop: 1 },

  // Detail grid
  gridRow:    { flexDirection: 'row', marginHorizontal: 16 },
  gridCell:   { flex: 1, borderWidth: 0.5, borderColor: MGRAY, paddingVertical: 3, paddingHorizontal: 5 },
  cellLabel:  { fontSize: 6.5, color: DGRAY, marginBottom: 1, textAlign: 'center' },
  cellValue:  { fontSize: 8.2, color: BLACK, textAlign: 'center' },

  // Table
  tableRow:   { flexDirection: 'row', marginHorizontal: 16 },
  labelCell:  { width: '22%', backgroundColor: LGRAY, borderWidth: 0.5, borderColor: MGRAY, padding: '3 5' },
  valueCell:  { width: '28%', borderWidth: 0.5, borderColor: MGRAY, padding: '3 5' },
  labelTxt:   { fontSize: 7, color: DGRAY },
  valueTxt:   { fontSize: 7.8, color: BLACK },

  // Notes
  notesBox:   { marginHorizontal: 16, borderWidth: 0.5, borderColor: MGRAY, minHeight: 28, padding: 5 },
  notesTxt:   { fontSize: 7.8, color: BLACK, lineHeight: 1.35 },

  // Route
  routeWrap:   { marginHorizontal: 16, borderWidth: 0.5, borderColor: MGRAY },
  routeFlow:   { flexDirection: 'column', padding: '2 5' },
  routeCityRow: { flexDirection: 'row', borderBottomWidth: 0.35, borderBottomColor: MGRAY, paddingVertical: 2.2 },
  routeCityIndex: { width: 16, fontSize: 6.5, color: WHITE, backgroundColor: BLUE, textAlign: 'center', paddingVertical: 1.2, marginRight: 5 },
  routeCityMain: { width: '24%', paddingRight: 5 },
  routeCityName: { fontSize: 7.8, color: NAVY },
  routeCityRole: { fontSize: 6.1, color: DGRAY, marginBottom: 0.5 },
  routeSiteCell: { width: '28%', paddingHorizontal: 5 },
  routeStopsCell: { flex: 1, paddingLeft: 5 },
  routeMetaLabel: { fontSize: 6.1, color: DGRAY, marginBottom: 0.5 },
  routeMetaValue: { fontSize: 6.8, color: BLACK, lineHeight: 1.25 },
  routeMetaMuted: { fontSize: 6.8, color: DGRAY, lineHeight: 1.25 },

  // Charges
  chargeRow:  { flexDirection: 'row', marginHorizontal: 16 },
  chargeCell: { flex: 1, borderWidth: 0.5, borderColor: MGRAY, padding: '5 6', alignItems: 'center' },
  chargeLbl:  { fontSize: 7, color: DGRAY, marginBottom: 2 },
  chargeVal:  { fontSize: 9, color: BLACK },
  totalCell:  { flex: 1, borderWidth: 0.5, borderColor: MGRAY, padding: '5 6', alignItems: 'center', backgroundColor: NAVY },
  totalLbl:   { fontSize: 7, color: '#90A4CC', marginBottom: 2 },
  totalVal:   { fontSize: 9, color: WHITE },

  // Signatures
  sigRow:     { flexDirection: 'row', marginHorizontal: 16 },
  sigCell:    { flex: 1, borderWidth: 0.5, borderColor: MGRAY, minHeight: 36, padding: '4 7' },
  sigTitle:   { fontSize: 7.2, color: NAVY, textAlign: 'center', marginBottom: 9 },
  sigLine:    { borderBottomWidth: 0.7, borderBottomColor: MGRAY, marginHorizontal: 8, marginTop: 'auto' },
  sigSub:     { fontSize: 6.4, color: MGRAY, textAlign: 'center', marginTop: 1.5 },

  // Spacer
  gap4:       { marginTop: 4 },
  gap6:       { marginTop: 6 },
  gap8:       { marginTop: 8 },

  // Footer
  footer:     { position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: NAVY, padding: '7 16' },
  footerSub:  { fontSize: 7, color: '#A0B0D0', textAlign: 'center' },
  footerName: { fontSize: 8.5, color: WHITE, textAlign: 'center', marginTop: 2 },

  // RTL helpers
  rtlText:    { textAlign: 'right' },
  rtlRow:     { flexDirection: 'row-reverse' },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────
const gap = (n) => <View style={{ marginTop: n }} />;
const sep = () => <View style={S.divider} />;

const SectionBar = ({ label, isAr = false }) => (
  <View style={[S.secBar, { marginHorizontal: 16 }]}>
    <Text style={[S.secText, isAr ? S.rtlText : {}]}>{safePdfText(label)}</Text>
  </View>
);

// ─── Document ─────────────────────────────────────────────────────────────────
const BillOfLadingDocument = ({ shipment, company, companySettings, options = {} }) => {
  const { language = 'en', includeAmount = false } = options;
  const isAr = language === 'ar';

  // ── Data helpers ──
  const L = (en, ar) => isAr ? ar : en;
  const fmt = (v) => safePdfText(v);
  // Title-case a string, handling snake/kebab/space separators. Leaves Arabic untouched.
  const titleCase = (v) => {
    if (!v || typeof v !== 'string') return v;
    if (/[\u0600-\u06FF]/.test(v)) return v;
    return v
      .replace(/[_-]+/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };
  const tc = (v) => (v && v !== '—' ? titleCase(v) : v);
  const textStyle = isAr ? S.rtlText : {};
  const rowStyle = (base) => isAr ? [base, S.rtlRow] : base;
  const cityName = (value) => {
    if (!value) return '';
    const city = saudiCities.find(c => c.value === value);
    return city?.label?.[language] || city?.label?.en || tc(value);
  };
  const stopName = (stop) => {
    if (!stop) return '';
    if (typeof stop === 'string') return safePdfText(stop, '').trim();

    const code = safePdfText(stop.code || stop.number || stop.branchNumber || stop.branchCode || '', '');
    const name = safePdfText(stop.name || stop.label || stop.value || stop.branchName || stop.site || stop.description || '', '');
    return [code, name].filter(Boolean).join(' - ').trim();
  };
  const routeItems = Array.isArray(shipment.routeCities)
    ? shipment.routeCities
        .map((city) => {
          const rawStops = Array.isArray(city.stops) ? city.stops : [];
          return {
            city: safePdfText(cityName(city.city), ''),
            site: safePdfText(city.site || '', ''),
            stopCount: rawStops.length,
            stops: rawStops.map(stopName).filter(Boolean),
          };
        })
        .filter(item => item.city || item.site || item.stopCount > 0 || item.stops.length > 0)
    : [];
  const firstRoute = routeItems[0];
  const lastRoute = routeItems[routeItems.length - 1];
  const routeTextSeparator = isAr ? '، ' : ', ';
  const RoutePoint = ({ item, index }) => {
    const isFirst = index === 0;
    const isLast = index === routeItems.length - 1;
    const role = isFirst
      ? labels.from
      : (isLast ? labels.to : labels.via);
    const hasStopValues = item.stops.length > 0;
    const stopCount = Math.max(item.stopCount || 0, item.stops.length);
    const hasStops = stopCount > 0;
    const stopsText = hasStopValues
      ? item.stops.map((stop, stopIndex) => `${stopIndex + 1}) ${stop}`).join(routeTextSeparator)
      : (stopCount > 0 ? safePdfText(`${stopCount} ${labels.locationsCount}`) : '-');
    const routeRowStyle = rowStyle(S.routeCityRow);
    const indexStyle = isAr ? [S.routeCityIndex, { marginRight: 0, marginLeft: 5 }] : S.routeCityIndex;
    const cityMainStyle = isAr ? [S.routeCityMain, { paddingRight: 0, paddingLeft: 5 }] : S.routeCityMain;
    const siteCellStyle = isAr ? [S.routeSiteCell, { paddingHorizontal: 5 }] : S.routeSiteCell;
    const stopsCellStyle = isAr ? [S.routeStopsCell, { paddingLeft: 0, paddingRight: 5 }] : S.routeStopsCell;

    return (
      <View style={[routeRowStyle, index === routeItems.length - 1 ? { borderBottomWidth: 0 } : {}]}>
        <Text style={indexStyle}>{safePdfText(index + 1)}</Text>
        <View style={cityMainStyle}>
          <Text style={[S.routeCityRole, textStyle]}>{safePdfText(role)}</Text>
          <Text style={[S.routeCityName, textStyle]}>{safePdfText(item.city || labels.routePoint)}</Text>
        </View>
        <View style={siteCellStyle}>
          <Text style={[S.routeMetaLabel, textStyle]}>{safePdfText(labels.site)}</Text>
          <Text style={[S.routeMetaValue, textStyle]}>{safePdfText(item.site || '-')}</Text>
        </View>
        <View style={stopsCellStyle}>
          {hasStops && (
            <Text style={[S.routeMetaLabel, textStyle]}>{safePdfText(`${labels.stops} (${stopCount})`)}</Text>
          )}
          <Text style={[hasStopValues ? S.routeMetaValue : S.routeMetaMuted, textStyle]}>{safePdfText(stopsText)}</Text>
        </View>
      </View>
    );
  };

  // ── Shipment data ──
  const shipNum      = fmt(shipment.shipmentNumber || shipment.trackingNumber || shipment.id);
  const clientName   = fmt(shipment.clientName);
  const originCity   = fmt(firstRoute?.city || cityName(shipment.originCity) || shipment.origin);
  const destCity     = fmt(lastRoute?.city || (
    shipment.destinationCities?.length
      ? shipment.destinationCities.map(cityName).join(', ')
      : cityName(shipment.destinationCity) || shipment.destination
  ));
  const pickupDate   = fmt(shipment.pickupDate     ? new Date(shipment.pickupDate).toLocaleDateString('en-GB')     : shipment.createdAt?.toDate ? new Date(shipment.createdAt.toDate()).toLocaleDateString('en-GB') : null);
  const deliveryDate = fmt(shipment.expectedDeliveryDate ? new Date(shipment.expectedDeliveryDate).toLocaleDateString('en-GB') : null);
  const vehiclePlate = fmt(shipment.vehiclePlate || shipment.truckPlate);
  const driverName   = fmt(shipment.driverName && shipment.driverName !== 'Unknown Driver' ? shipment.driverName : null);
  const notes        = safePdfText(shipment.notes || '', '');
  const serviceType  = tc(fmt(shipment.serviceType  || shipment.shipmentType));
  const pickupAddr   = fmt(shipment.pickupAddress || firstRoute?.site || shipment.pickupSite);
  const deliveryAddr = fmt(shipment.deliveryAddress || lastRoute?.site || shipment.dropoffSite);
  const status       = tc(fmt(shipment.status));
  const docDate      = new Date().toLocaleDateString('en-GB');

  // ── Company data ──
  const companyName  = (
    isAr
      ? companySettings?.companyName || companySettings?.companyNameEn
      : companySettings?.companyNameEn || companySettings?.companyName
  ) || company?.companyName || company?.name || 'Company';
  const safeCompanyName = safePdfText(companyName, 'Company');
  const companyAddr  = safePdfText(companySettings?.address || company?.address || '', '');
  const companyPhone = safePdfText(companySettings?.phone   || company?.phone   || '', '');
  const companyEmail = safePdfText(companySettings?.email || company?.email || '', '');
  const companyWebsite = safePdfText(companySettings?.website || company?.website || '', '');
  const companyVAT   = safePdfText(companySettings?.vatId || companySettings?.vatNumber || company?.vatId || '', '');
  const logoSrc      = companySettings?.logoBase64 || companySettings?.logoUrl || company?.logoBase64 || company?.logoUrl || null;
  const companyInfoSeparator = isAr ? '، ' : '  |  ';
  const contactLine = safePdfText([companyPhone, companyEmail, companyWebsite].filter(Boolean).join(companyInfoSeparator), '');
  const registrationLine = safePdfText([companyAddr, companyVAT].filter(Boolean).join(companyInfoSeparator), '');

  // ── Pricing ──
  const basePrice = parseFloat(shipment.cost || shipment.basePrice || 0);
  const backhaul  = parseFloat(shipment.backhaulPrice || 0);
  const vat       = parseFloat(shipment.vatAmount || basePrice * 0.15);
  const total     = basePrice + backhaul + vat;

  // ── Bilingual labels ──
  const labels = {
    bol:          L('BILL OF LADING',        'بوليصة الشحن'),
    docNo:        L('Document No',           'رقم المستند'),
    issued:       L('Issued',                'تاريخ الإصدار'),
    shippedBy:    L('SHIPPED BY',            'المُرسِل'),
    deliveredTo:  L('DELIVERED TO',          'المُرسَل إليه'),
    shipDetails:  L('SHIPMENT DETAILS',      'تفاصيل الشحنة'),
    origin:       L('Origin',                'المنشأ'),
    destination:  L('Destination',           'الوجهة'),
    pickupDate:   L('Pickup Date',           'تاريخ الاستلام'),
    delivDate:    L('Delivery Date',         'تاريخ التسليم'),
    serviceType:  L('Service Type',          'نوع الخدمة'),
    status:       L('Status',                'الحالة'),
    transport:    L('TRANSPORT DETAILS',     'تفاصيل النقل'),
    routeDetails: L('ROUTE DETAILS',         'تفاصيل المسار'),
    routePoint:   L('Route Point',           'نقطة المسار'),
    from:         L('From',                  'من'),
    to:           L('To',                    'إلى'),
    via:          L('Via',                   'عبر'),
    site:         L('Site / Branch',         'الموقع / الفرع'),
    noSite:       L('No site specified',     'لم يحدد موقع'),
    stops:        L('Stops',                 'المواقع'),
    locationsCount: L('locations',           'مواقع'),
    plate:        L('Vehicle Plate',         'لوحة المركبة'),
    driver:       L('Driver Name',           'اسم السائق'),
    pickupAddr:   L('Pickup Address',        'عنوان الاستلام'),
    delivAddr:    L('Delivery Address',      'عنوان التسليم'),
    originCity:   L('Origin City',           'مدينة المنشأ'),
    destCity:     L('Destination City',      'مدينة الوجهة'),
    notes:        L('NOTES & INSTRUCTIONS',  'ملاحظات وتعليمات'),
    charges:      L('CHARGES',              'الرسوم'),
    baseFreight:  L('Base Freight',         'الأجرة الأساسية'),
    backhaul:     L('Backhaul',             'رسوم العودة'),
    vat15:        L('VAT (15%)',            'ضريبة القيمة المضافة'),
    totalLabel:   L('TOTAL',               'المجموع'),
    signatures:   L('ACKNOWLEDGMENT & SIGNATURES', 'التوقيعات والإقرار'),
    shipper:      L('Shipper / Sender',    'المُرسِل'),
    carrier:      L('Carrier / Driver',    'الناقل / السائق'),
    receiver:     L('Receiver',            'المستلم'),
    sigNote:      L('Signature & Date',    'التوقيع والتاريخ'),
    footerNote:   L("This document is a legal shipping record. All goods are subject to the carrier's standard terms & conditions.", 'هذه الوثيقة سجل شحن قانوني. جميع البضائع خاضعة للشروط والأحكام القياسية للناقل.'),
    tel:          L('Tel', 'هاتف'),
    website:      L('Website', 'الموقع الإلكتروني'),
    vat:          L('VAT No', 'الرقم الضريبي'),
    delivAddress: L('Delivery Address', 'عنوان التسليم'),
    phone:        L('Phone', 'الهاتف'),
  };

  const titleLine = isAr ? labels.bol : `BILL OF LADING  |  ${labels.bol}`;

  return (
    <Document>
      <Page size="A4" style={S.page}>

        {/* ── Top accent bar ── */}
        <View style={S.topBar} />

        {/* ── Header ── */}
        <View style={rowStyle(S.headerRow)}>
          {/* Logo + Company info */}
          <View style={{ flexDirection: isAr ? 'row-reverse' : 'row', flex: 1, alignItems: 'flex-start' }}>
            {logoSrc ? (
              <Image src={logoSrc} style={S.logoImg} />
            ) : (
              <View style={S.logoPlaceholder}>
                <Text style={{ fontSize: 7, color: DGRAY }}>{isAr ? 'الشعار' : 'LOGO'}</Text>
              </View>
            )}
            <View style={S.companyBlock}>
              <Text style={[S.companyName, textStyle]}>{safeCompanyName}</Text>
              {contactLine && <Text style={[S.companyMetaLine, textStyle]}>{contactLine}</Text>}
              {registrationLine && <Text style={[S.companyMetaLine, textStyle]}>{registrationLine}</Text>}
            </View>
          </View>

          {/* Barcode */}
          <View style={S.barcodeBlock}>
            {options.barcodeDataUrl && (
              <Image src={options.barcodeDataUrl} style={S.barcodeImg} />
            )}
            <Text style={S.barcodeNum}>{safePdfText(shipNum)}</Text>
          </View>
        </View>

        {sep()}

        {/* ── Title bar ── */}
        {gap(6)}
        <View style={S.titleBar}>
          <Text style={S.titleText}>{safePdfText(titleLine)}</Text>
        </View>

        {/* ── Doc meta ── */}
        <View style={rowStyle(S.metaBar)}>
          <Text style={S.metaText}>{safePdfText(`${labels.docNo}: ${shipNum}`)}</Text>
          <Text style={[S.metaText, { color: DGRAY }]}>{safePdfText(`${labels.issued}: ${docDate}`)}</Text>
        </View>

        {/* ── Party row ── */}
        {gap(6)}
        <View style={rowStyle(S.partyRow)}>
          {/* Shipper */}
          <View style={[S.partyBox, isAr ? { marginLeft: 4 } : { marginRight: 4 }]}>
            <View style={S.partyHead}>
              <Text style={S.partyHeadTxt}>{safePdfText(labels.shippedBy)}</Text>
            </View>
            <View style={S.partyBody}>
              <Text style={[S.partyName, textStyle]}>{clientName}</Text>
              {shipment.clientPhone && <Text style={[S.partySub, textStyle]}>{safePdfText(`${labels.phone}: ${shipment.clientPhone}`)}</Text>}
            </View>
          </View>
          {/* Consignee */}
          <View style={[S.partyBox, isAr ? { marginRight: 4 } : { marginLeft: 4 }]}>
            <View style={S.partyHead}>
              <Text style={S.partyHeadTxt}>{safePdfText(labels.deliveredTo)}</Text>
            </View>
            <View style={S.partyBody}>
              <Text style={[S.partySub, textStyle]}>{safePdfText(labels.delivAddress)}</Text>
              <Text style={[S.partyName, { marginTop: 3, fontSize: 8.5 }, textStyle]}>{deliveryAddr}</Text>
            </View>
          </View>
        </View>

        {/* ── Shipment Details ── */}
        {gap(5)}
        <SectionBar label={labels.shipDetails} isAr={isAr} />
        {/* Row 1 */}
        <View style={rowStyle(S.gridRow)}>
          {[
            [labels.origin,      originCity  ],
            [labels.destination, destCity    ],
            [labels.pickupDate,  pickupDate  ],
          ].map(([lbl, val], i) => (
            <View key={i} style={[S.gridCell, { backgroundColor: LGRAY }]}>
              <Text style={S.cellLabel}>{safePdfText(lbl)}</Text>
              <Text style={S.cellValue}>{safePdfText(val)}</Text>
            </View>
          ))}
        </View>
        {/* Row 2 */}
        <View style={rowStyle(S.gridRow)}>
          {[
            [labels.delivDate,   deliveryDate],
            [labels.serviceType, serviceType ],
            [labels.status,      status      ],
          ].map(([lbl, val], i) => (
            <View key={i} style={[S.gridCell, { backgroundColor: WHITE }]}>
              <Text style={S.cellLabel}>{safePdfText(lbl)}</Text>
              <Text style={S.cellValue}>{safePdfText(val)}</Text>
            </View>
          ))}
        </View>

        {/* ── Transport Details ── */}
        {gap(5)}
        <SectionBar label={labels.transport} isAr={isAr} />
        {[
          [labels.plate,     vehiclePlate, labels.driver,    driverName  ],
          [labels.pickupAddr, pickupAddr,  labels.delivAddr, deliveryAddr],
          [labels.originCity, originCity,  labels.destCity,  destCity    ],
        ].map(([l1, v1, l2, v2], i) => (
          <View key={i} style={rowStyle(S.tableRow)}>
            <View style={[S.labelCell, i % 2 ? {} : {}]}><Text style={[S.labelTxt, textStyle]}>{safePdfText(l1)}</Text></View>
            <View style={[S.valueCell, { backgroundColor: i % 2 === 0 ? LGRAY : WHITE }]}><Text style={[S.valueTxt, textStyle]}>{safePdfText(v1)}</Text></View>
            <View style={S.labelCell}><Text style={[S.labelTxt, textStyle]}>{safePdfText(l2)}</Text></View>
            <View style={[S.valueCell, { backgroundColor: i % 2 === 0 ? LGRAY : WHITE }]}><Text style={[S.valueTxt, textStyle]}>{safePdfText(v2)}</Text></View>
          </View>
        ))}

        {/* ── Route Details ── */}
        {routeItems.length > 0 && (
          <>
            {gap(5)}
            <SectionBar label={labels.routeDetails} isAr={isAr} />
            <View style={S.routeWrap}>
              <View style={S.routeFlow}>
                {routeItems.map((item, index) => (
                  <View key={index}>
                    <RoutePoint item={item} index={index} />
                  </View>
                ))}
              </View>
            </View>
          </>
        )}

        {/* ── Notes ── */}
        {gap(5)}
        <SectionBar label={labels.notes} isAr={isAr} />
        <View style={S.notesBox}>
          <Text style={[S.notesTxt, textStyle]}>{notes.trim() || (isAr ? 'لا توجد تعليمات خاصة.' : 'No special instructions.')}</Text>
        </View>

        {/* ── Charges (optional) ── */}
        {includeAmount && (
          <>
            {gap(5)}
            <SectionBar label={labels.charges} isAr={isAr} />
            <View style={rowStyle(S.chargeRow)}>
              {[
                [labels.baseFreight, `SAR ${basePrice.toFixed(2)}`],
                [labels.backhaul,    backhaul > 0 ? `SAR ${backhaul.toFixed(2)}` : '—'],
                [labels.vat15,       `SAR ${vat.toFixed(2)}`],
              ].map(([lbl, val], i) => (
                <View key={i} style={S.chargeCell}>
                  <Text style={S.chargeLbl}>{safePdfText(lbl)}</Text>
                  <Text style={S.chargeVal}>{safePdfText(val)}</Text>
                </View>
              ))}
              <View style={S.totalCell}>
                <Text style={S.totalLbl}>{safePdfText(labels.totalLabel)}</Text>
                <Text style={S.totalVal}>{safePdfText(`SAR ${total.toFixed(2)}`)}</Text>
              </View>
            </View>
          </>
        )}

        {/* ── Signatures ── */}
        {gap(5)}
        <SectionBar label={labels.signatures} isAr={isAr} />
        <View style={rowStyle(S.sigRow)}>
          {[labels.shipper, labels.carrier, labels.receiver].map((title, i) => (
            <View key={i} style={S.sigCell}>
              <Text style={S.sigTitle}>{safePdfText(title)}</Text>
              <View style={S.sigLine} />
              <Text style={S.sigSub}>{safePdfText(labels.sigNote)}</Text>
            </View>
          ))}
        </View>

        {/* ── Footer ── */}
        <View style={S.footer}>
          <Text style={S.footerSub}>{safePdfText(labels.footerNote)}</Text>
          <Text style={S.footerName}>{safeCompanyName}</Text>
        </View>

      </Page>
    </Document>
  );
};

export default BillOfLadingDocument;
